import { CreateOffice, ViewOfficeList } from '@/features/admin/components/administration';
import { createFileRoute, Navigate, useLocation } from '@tanstack/react-router';
import useGetOfficeList from '@/features/office/hooks/useGetOfficeList';
import { useState, useMemo } from 'react';
import { useAuth } from '@/features/auth/providers/AuthProvider';
import useGetManagedOffices from '@/features/user/hooks/useGetManagedOffices';
import { ErrorState } from '@/shared/components/feedback';
import { AdminHeader, AdminPageLayout } from '@/shared/components/ui/AdminHeader';
import { Building } from 'lucide-react';

export const Route = createFileRoute('/admin/offices/')({
  component: RouteComponent,
});

function RouteComponent() {
  const allOffices = useGetOfficeList();
  const [open, setOpen] = useState(false);
  const { user, loading: authLoading, roleReady } = useAuth();
  const role = user?.role;
  const { managedOffices, error: managedOfficesError } = useGetManagedOffices(user?.uid);
  const location = useLocation();

  // Filter offices for office managers - they only see their managed offices
  // Admins see all offices (must be called before early returns)
  const data = useMemo(() => {
    if (!allOffices) return [];

    if (role === 'officeManager' && managedOffices.length > 0) {
      return allOffices.filter(office => managedOffices.includes(office.id));
    }

    // Admins and regular users see all offices
    return allOffices;
  }, [allOffices, role, managedOffices]);

  const header = (
    <AdminHeader
      icon={Building}
      title="Office Management"
      description="Manage all your office locations and configurations in one place"
      variant="emerald"
    />
  );

  if (authLoading || !roleReady) return null;

  if (!user) {
    return <Navigate to="/login" search={{ from: location.pathname }} replace />;
  }

  if (role !== 'admin' && role !== 'officeManager') {
    return <Navigate to="/" replace />;
  }

  if (managedOfficesError) {
    return (
      <AdminPageLayout header={header}>
        <ErrorState
          title="Failed to load permissions"
          description="We couldn't verify your permissions. Please try again."
        />
      </AdminPageLayout>
    );
  }

  return (
    <>
      <ViewOfficeList data={data || []} onAddOffice={() => setOpen(true)} />
      <CreateOffice
        open={open}
        onConfirm={() => setOpen(false)}
        onOpenChange={() => setOpen(false)}
      />
    </>
  );
}
